import React from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiCheck, FiX, FiLogIn, FiPhone } from 'react-icons/fi';
import useSignUp from './useSignUp';
import { SignUpContainer, Form, FormGroup, InputWrapper, Input, Icon, PasswordToggle, Button, AlertIcon, AlertText, ErrorMessage, SuccessMessage, Divider, LoginLink, LoadingSpinner, PasswordRequirements, Requirement, RequirementIcon, EmailMatchIndicator, EmailExistsMessage, EmailLoginLink, Title, CheckingEmailText } from './SignUp.styles';
// Note: SignUp is rendered inside AuthContainer/AuthCard from App.js
// So we don't need AuthPage wrapper here, just render content directly

function SignUp() {
  const {
    formData,
    showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword,
    loading,
    error,
    success,
    emailExists,
    checkingEmail,
    location,
    passwordRequirements,
    handleChange,
    handleSubmit,
  } = useSignUp();

  return (
    <SignUpContainer>
          <Title>Create Account</Title>
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiUser size={16} />
                </Icon>
                <Input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiUser size={16} />
                </Icon>
                <Input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
              {checkingEmail && (
                <CheckingEmailText>
                  Checking email availability...
                </CheckingEmailText>
              )}
              {emailExists && formData.email && !checkingEmail && (() => {
                const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
                const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
                const redirect = redirectFromQuery || redirectFromStorage;
                const loginUrl = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
                
                return (
                  <EmailExistsMessage>
                    <FiLogIn size={14} />
                    An account with this email already exists.
                    <EmailLoginLink to={loginUrl}>
                      <FiLogIn size={12} />
                      Login here
                    </EmailLoginLink>
                  </EmailExistsMessage>
                );
              })()}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="confirmEmail"
                  placeholder="Confirm email address"
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  required
                />
              </InputWrapper>
              {formData.confirmEmail && formData.email && (
                <EmailMatchIndicator matches={formData.email === formData.confirmEmail}>
                  {formData.email === formData.confirmEmail ? (
                    <>
                      <FiCheck size={14} />
                      Email addresses match
                    </>
                  ) : (
                    <>
                      <FiX size={14} />
                      Email addresses do not match
                    </>
                  )}
                </EmailMatchIndicator>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiPhone size={16} />
                </Icon>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <PasswordRequirements>
              <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Password Requirements:
              </div>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least 10 characters
              </Requirement>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least one digit (0-9)
              </Requirement>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least one special character (!@#$%^&*)
              </Requirement>
            </PasswordRequirements>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
              {formData.password && (
                <PasswordRequirements>
                  <Requirement className={passwordRequirements.length ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.length}>✓</RequirementIcon>
                    At least 10 characters
                  </Requirement>
                  <Requirement className={passwordRequirements.digit ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.digit}>✓</RequirementIcon>
                    At least one digit
                  </Requirement>
                  <Requirement className={passwordRequirements.specialChar ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.specialChar}>✓</RequirementIcon>
                    At least one special character
                  </Requirement>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                role="alert"
              >
                <AlertIcon type="error">
                  <FiX />
                </AlertIcon>
                <AlertText type="error">{error}</AlertText>
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                role="alert"
              >
                <AlertIcon type="success">
                  <FiCheck />
                </AlertIcon>
                <AlertText type="success">{success}</AlertText>
              </SuccessMessage>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </Form>

          <Divider>
            <span>or</span>
          </Divider>

          <LoginLink>
            Already have an account?{' '}
            {(() => {
              const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
              const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
              const redirect = redirectFromQuery || redirectFromStorage;
              const loginUrl = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
              return <Link to={loginUrl}>Sign in here</Link>;
            })()}
          </LoginLink>
        </SignUpContainer>
  );
}

export default SignUp;
